import { useEffect, useState } from 'react';
import { Button, InputPlain } from '@peacecover/component';
import { ArrowLeft, ArrowRightSmall } from '@peacecover/icon';
import { useVirtualKeyboardVisible } from '../../chat/hooks/useVirtualKeyboardVisible';
import { getJosa } from '../../../utils/bettieUtils';
import AnimationStarBeam from './AnimationStarBeam';

type PersonaInputStepSlot = 'first' | 'second';

type PersonaInputStepProps = {
  slot: PersonaInputStepSlot;
  onBack: () => void;
  onSetAsMe: () => void;
  nickname: string;
  onNicknameChange: (value: string) => void;
  onNext: () => void;
  /** 두 번째 슬롯일 때, 첫 인물이 User면 '나로 설정' 버튼 숨김 */
  isFirstPersonaUser?: boolean;
  /** 두 번째 슬롯일 때, 첫 번째 인물 닉네임 (첫 번째 문장에 표시) */
  firstPersonaNickname?: string;
};

const TITLE_BY_SLOT: Record<PersonaInputStepSlot, string> = {
  first: '첫번째 주인공을 설정해볼까요?',
  second: '두번째 주인공을 설정해볼까요?'
};

const SET_AS_ME_LABEL_BY_SLOT: Record<PersonaInputStepSlot, string> = {
  first: '첫번째 주인공을 나로 설정하기',
  second: '두번째 주인공을 나로 설정하기'
};

const PersonaInputStep = ({
  slot,
  onBack,
  onSetAsMe,
  nickname,
  onNicknameChange,
  onNext,
  isFirstPersonaUser = false,
  firstPersonaNickname = ''
}: PersonaInputStepProps) => {
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  // 입력 도중 조사 깜빡임을 줄이기 위해, 일정 시간 입력이 멈춘 뒤에만 조사 계산
  const [settledNickname, setSettledNickname] = useState(nickname);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSettledNickname(nickname);
    }, 300);
    return () => window.clearTimeout(timeoutId);
  }, [nickname]);

  const firstLineNicknameForJosa = slot === 'first' ? settledNickname : firstPersonaNickname;

  const titleLabel = TITLE_BY_SLOT[slot];
  const setAsMeLabel = SET_AS_ME_LABEL_BY_SLOT[slot];
  const showSetAsMeButton = slot === 'first' || !isFirstPersonaUser;
  const isNextDisabled = !nickname.trim();

  return (
    <div className="bg-background-dark min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] overflow-x-hidden relative">
      <header className="px-[16px] py-[15px] z-[1] relative">
        <button type="button" onClick={onBack}>
          <ArrowLeft size={24} className="text-pure-white" />
        </button>
      </header>
      <div
        className="rounded-full w-[1020px] h-[1020px] absolute top-[-798px] left-1/2 -translate-x-1/2 flex flex-col justify-end items-center"
        style={{
          background:
            'radial-gradient(50% 50% at 50% 50%, #0D0E12 23.08%, #0D0E12 73.08%, #130757 92.31%, #19009C 100%)'
        }}
      >
        <AnimationStarBeam />
        <div
          className="text-pure-white mb-[50px] flex flex-col items-center"
          style={{
            fontFamily: '"Noto Serif KR", ui-serif, Georgia, "Times New Roman", serif'
          }}
        >
          <div className="flex items-center gap-[3px]">
            {slot === 'first' ? (
              <div className="min-w-[38px] h-[19px] text-center text-[16px] flex items-center justify-center leading-[28px] text-pure-white rounded-[6px] bg-[rgba(0,0,0,0.14)] shadow-[0_0_12px_0_#FFF]">
                {nickname || ''}
              </div>
            ) : (
              <span className="text-[16px] leading-[28px]">{firstPersonaNickname || ''}</span>
            )}
            {getJosa(firstLineNicknameForJosa, '이/가') || '가'} 먼저 움직였고,
          </div>
          <div className="flex items-center">
            {slot === 'first' ? (
              <div className="text-[16px] leading-[28px]" style={{ filter: 'blur(5.5px)' }}>
                쇼타로
              </div>
            ) : (
              <div className="min-w-[38px] h-[19px] text-center text-[16px] flex items-center justify-center leading-[28px] text-pure-white rounded-[6px] bg-[rgba(0,0,0,0.14)] shadow-[0_0_12px_0_#FFF]">
                {nickname || ''}
              </div>
            )}
            {getJosa(slot === 'first' ? '쇼타로' : settledNickname, '은/는') || '는'} 거기에 맞춰
            움직였다.
          </div>
        </div>
      </div>
      <div className="mt-[199px]">
        <h1 className="px-[24px] py-[10px] text-[26px] font-semibold leading-[31px] text-pure-white text-center">
          이야기에 등장할
          <br />
          {titleLabel}
        </h1>
        {showSetAsMeButton && (
          <div className="flex justify-end mt-[18px] px-[24px]">
            <button
              type="button"
              className="bg-[linear-gradient(135deg,#15CCCB_-14.28%,#4987FF_42.83%,#8B1CFA_101.7%)] rounded-[30px] py-[4px] pl-[8px] pr-[4px] flex items-center gap-[2px]"
              onClick={onSetAsMe}
            >
              <p className="text-[11px] font-semibold leading-[13px] text-pure-white">
                {setAsMeLabel}
              </p>
              <ArrowRightSmall size={13} className="text-pure-white" />
            </button>
          </div>
        )}
        <div className="px-[16px] py-[17px] mt-[10px] bg-secondary-dark rounded-[12px] mx-[24px]">
          <InputPlain
            placeholder="이름을 입력해주세요"
            className="bg-transparent placeholder:text-placeholder-dark !text-main-dark-2"
            value={nickname}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={(e) => onNicknameChange(e.target.value)}
          />
        </div>
        <ul className="text-[12px] text-main-dark-2 leading-[14px] mt-[16px] ml-[33px] flex flex-col items-start gap-[4px] list-disc list-inside">
          <li>풀네임을 입력하면 가장 자연스러워요</li>
          <li>원작자 의도에 따라 성 없이 이름만 보여질 수도 있어요.</li>
        </ul>
      </div>
      <div
        className={`fixed bottom-0 left-0 right-0 px-[24px] pb-[calc(env(safe-area-inset-bottom)+17px)] transition-all duration-150 ease-in-out ${
          isKeyboardVisible
            ? 'translate-y-full opacity-0 pointer-events-none'
            : 'translate-y-0 opacity-100'
        }`}
        aria-hidden={isKeyboardVisible}
      >
        <Button
          size="large"
          className="w-full text-[17px] font-semibold"
          disabled={isNextDisabled}
          onClick={onNext}
        >
          다음
        </Button>
      </div>
    </div>
  );
};

export default PersonaInputStep;
